import { ChangeDetectionStrategy, Component, inject, input, signal, effect, computed } from '@angular/core';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { AccordionModule } from 'primeng/accordion';
import { TripCostSummary } from '../../models/trip-cost-summary';
import { ShipmentService } from '../../shipment.service';

@Component({
  selector: 'app-trip-cost-summary',
  imports: [CommonModule, TableModule, TagModule, CurrencyPipe, AccordionModule],
  template: `
    <div class="pt-3">
        <p-accordion [multiple]="true" class="block">
            <p-accordion-panel value="expenses">
                <p-accordion-header>
                    <div class="summary-accordion-header">
                        <strong>Expense Breakdown</strong>
                        <span>Buying cost distribution by expense type</span>
                    </div>
                </p-accordion-header>
                <p-accordion-content>
                    <p-table [value]="breakdownData()" responsiveLayout="scroll">
                        <ng-template pTemplate="header">
                            <tr>
                                <th>Expense Type</th>
                                <th class="text-right">Amount (SAR)</th>
                                <th class="text-right">% of Total Cost</th>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-item>
                            <tr>
                                <td class="font-medium">{{ item.type }}</td>
                                <td class="text-right">{{ item.amount | currency:'SAR ':'':'1.2-2' }}</td>
                                <td class="text-right">
                                    <p-tag [value]="item.percentage + '%'" [severity]="item.severity"></p-tag>
                                </td>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="emptymessage">
                            <tr>
                                <td colspan="3" class="text-center py-4">No cost breakdown available.</td>
                            </tr>
                        </ng-template>
                    </p-table>
                </p-accordion-content>
            </p-accordion-panel>

            <p-accordion-panel value="workforce-finance-cash">
                <p-accordion-header>
                    <div class="summary-accordion-header">
                        <strong>Workforce Finance Cash Rollup</strong>
                        <span>Advance issue, settlement, and unreconciled cash exposure</span>
                    </div>
                </p-accordion-header>
                <p-accordion-content>
                    <p-table [value]="workforceCashRows()" responsiveLayout="scroll">
                        <ng-template pTemplate="header">
                            <tr>
                                <th>Workforce Finance Cash Metric</th>
                                <th class="text-right">Amount (SAR)</th>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-item>
                            <tr>
                                <td class="font-medium">{{ item.type }}</td>
                                <td class="text-right">{{ item.amount | currency:'SAR ':'':'1.2-2' }}</td>
                            </tr>
                        </ng-template>
                    </p-table>
                </p-accordion-content>
            </p-accordion-panel>
        </p-accordion>
    </div>
  `,
  styles: [`
    .summary-accordion-header {
      display: grid;
      gap: 0.2rem;
    }

    .summary-accordion-header span {
      color: #64748b;
      font-size: 0.85rem;
      font-weight: 500;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TripCostSummaryComponent {
    private shipmentService = inject(ShipmentService);
    
    public shipmentId = input.required<number>();
    public refreshKey = input<number>(0);
    public summary = signal<TripCostSummary | null>(null);

    constructor() {
        effect(() => {
            const id = this.shipmentId();
            this.refreshKey();
            if (id) {
                this.shipmentService.getTripCostSummary(id).subscribe(data => {
                    this.summary.set(data);
                });
            }
        });
    }

    public breakdownData = computed(() => {
        const s = this.summary();
        if (!s) return [];

        const totalCost = s.totalBuyingCost || 1; // avoid div by zero

        return [
            { type: 'Transport Base', amount: s.transportCost, percentage: Math.round((s.transportCost/totalCost)*100), severity: 'info' },
            { type: 'Fuel Surcharge', amount: s.fuelCost, percentage: Math.round((s.fuelCost/totalCost)*100), severity: 'warning' },
            { type: 'Tolls/Gate Fees', amount: s.tollCost, percentage: Math.round((s.tollCost/totalCost)*100), severity: 'secondary' },
            { type: 'Loading/Offloading', amount: s.loadingCharges, percentage: Math.round((s.loadingCharges/totalCost)*100), severity: 'help' },
            { type: 'Others', amount: s.otherCosts, percentage: Math.round((s.otherCosts/totalCost)*100), severity: 'contrast' }
        ].filter(item => item.amount > 0);
    });

    public workforceCashRows = computed(() => {
        const s = this.summary();
        return [
            { type: 'Allowance Issued', amount: s?.allowanceIssuedAmount || 0 },
            { type: 'Settled Workforce Expense', amount: s?.settledDriverExpenseAmount || 0 },
            { type: 'Pending Return', amount: s?.pendingReturnAmount || 0 },
            { type: 'Pending Top-up', amount: s?.pendingTopUpAmount || 0 },
            { type: 'Unreconciled Workforce Cash', amount: s?.unreconciledWorkforceCashAmount || 0 }
        ];
    });

}
